import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import type { QuickTaskDraftFormValues } from '../schemas';
import type { AiFieldKey, ProposalCandidate } from '../types';

interface TaskFieldCandidateSelectProps {
  field: AiFieldKey;
  candidates: ProposalCandidate[];
  control: Control<QuickTaskDraftFormValues>;
  testIdPrefix: string;
}

/**
 * The AI tab's ambiguous case: the model named something the board has more than one of, so the
 * choice is offered and never made. Deliberately not the field's normal control — that one would
 * present every value the board has, hiding which two the instruction was actually between.
 */
export function TaskFieldCandidateSelect({
  field,
  candidates,
  control,
  testIdPrefix,
}: TaskFieldCandidateSelectProps) {
  const { t } = useTranslation('ai');
  const isArray = field === 'tags';

  return (
    <Controller
      name={field}
      control={control}
      render={({ field: selectField }) => (
        <Select
          value={
            (isArray ? (selectField.value as string[])?.[0] : (selectField.value as string)) ?? ''
          }
          onValueChange={(value) => selectField.onChange(isArray ? [value] : value)}
        >
          <SelectTrigger data-testid={`${testIdPrefix}-${field}-candidates`}>
            <SelectValue placeholder={t('quickCreate.selectOption')} />
          </SelectTrigger>
          <SelectContent>
            {candidates.map((candidate) => (
              <SelectItem key={candidate.id} value={candidate.id}>
                {candidate.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  );
}
